class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    const debouncedSubmit = this.debouncedOnSubmit.bind(this);
    if (facetForm) {
      facetForm.addEventListener('input', debouncedSubmit);
    }

    const filterDrawer = document.getElementById('FilterDrawer');
    if (facetForm?.id === 'FacetFiltersForm' && filterDrawer && !FacetFiltersForm.drawerInputBound) {
      FacetFiltersForm.drawerInputBound = true;
      filterDrawer.addEventListener('keyup', onKeyUpEscape);
      filterDrawer.addEventListener('input', debouncedSubmit);
    } else if (!filterDrawer) {
      const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
      if (facetWrapper) {
        facetWrapper.addEventListener('keyup', onKeyUpEscape);
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a.js-facet-clear-all');
      if (!link) return;
      event.preventDefault();
      FacetFiltersForm.handleFilterRemove(link);
    });
  }

  static handleFilterRemove(link) {
    if (link instanceof HTMLElement && document.activeElement === link) {
      link.blur();
    }

    const href = link?.href || '';
    const searchParams = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(searchParams);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static beginFacetUpdate() {
    const filterDrawer = document.getElementById('FilterDrawer');
    const gridContainer = document.getElementById('ProductGridContainer');
    const facetsContainer = document.querySelector('.facets-container');
    const facetsForm = document.querySelector('.facets--filter-drawer .facets__form');

    if (filterDrawer) {
      filterDrawer._openFilterIds = [...filterDrawer.querySelectorAll('.facets__disclosure[open]')].map(
        (element) => element.id
      );
      const panel = filterDrawer.querySelector('.filter-drawer__panel');
      filterDrawer._scrollTop = panel?.scrollTop || 0;
      filterDrawer.classList.add('filter-drawer--updating');
    }

    if (gridContainer) {
      const collection = gridContainer.querySelector('.collection');
      const gridHeight = collection?.offsetHeight || 0;
      if (gridHeight > 0) {
        gridContainer.style.setProperty('--grid-min-height', `${gridHeight}px`);
      }
      gridContainer.classList.add('product-grid-container--updating');
    }

    if (facetsForm) {
      const toolbarHeight = facetsForm.offsetHeight;
      if (toolbarHeight > 0) {
        facetsForm.style.minHeight = `${toolbarHeight}px`;
      }
    }

    facetsContainer?.classList.add('facets-container--updating');
    FacetFiltersForm.toggleActiveFacets(true);
  }

  static endFacetUpdate() {
    const finish = () => {
      const filterDrawer = document.getElementById('FilterDrawer');
      const gridContainer = document.getElementById('ProductGridContainer');
      const facetsContainer = document.querySelector('.facets-container');
      const facetsForm = document.querySelector('.facets--filter-drawer .facets__form');

      if (filterDrawer) {
        filterDrawer._openFilterIds?.forEach((id) => {
          const element = document.getElementById(id);
          if (element) element.setAttribute('open', '');
        });

        const panel = filterDrawer.querySelector('.filter-drawer__panel');
        if (panel && typeof filterDrawer._scrollTop === 'number') {
          panel.scrollTop = filterDrawer._scrollTop;
        }

        filterDrawer.classList.remove('filter-drawer--updating');
        delete filterDrawer._openFilterIds;
        delete filterDrawer._scrollTop;

        if (window.filterDrawerInstance?.reinit) {
          window.filterDrawerInstance.reinit();
        }
      }

      if (gridContainer) {
        gridContainer.classList.remove('product-grid-container--updating');
        gridContainer.style.removeProperty('--grid-min-height');
      }

      if (facetsForm) {
        facetsForm.style.minHeight = '';
      }

      facetsContainer?.classList.remove('facets-container--updating');
      FacetFiltersForm.toggleActiveFacets(false);
      FacetFiltersForm.reinitPriceRanges();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(finish);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.beginFacetUpdate();

    const sections = FacetFiltersForm.getSections();
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, retries = 1) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(`Facet fetch failed: ${response.status}`);
          error.retryable = response.status === 429 || response.status >= 500;
          throw error;
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.endFacetUpdate();
      })
      .catch((error) => {
        if (error?.retryable && retries > 0) {
          setTimeout(() => FacetFiltersForm.renderSectionFromFetch(url, event, retries - 1), 1500);
          return;
        }
        FacetFiltersForm.endFacetUpdate();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.endFacetUpdate();
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newRoot = parsedHTML.getElementById('ProductGridContainer');
    const currentRoot = document.getElementById('ProductGridContainer');

    if (!newRoot || !currentRoot) return;

    const newCollection = newRoot.querySelector('.collection');
    const currentCollection = currentRoot.querySelector('.collection');
    const emptyStateChanged =
      Boolean(newCollection?.classList.contains('collection--empty')) !==
      Boolean(currentCollection?.classList.contains('collection--empty'));

    // Empty and populated grids use different wrappers; partial innerHTML breaks #product-grid.
    if (newCollection && currentCollection && !emptyStateChanged) {
      currentCollection.innerHTML = newCollection.innerHTML;
      currentCollection.className = newCollection.className;
      if (newCollection.id) {
        currentCollection.id = newCollection.id;
      } else {
        currentCollection.removeAttribute('id');
      }
    } else {
      currentRoot.innerHTML = newRoot.innerHTML;
    }

    currentRoot.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    if (typeof window.initCardMediaSliders === 'function') {
      window.initCardMediaSliders(currentRoot);
    }
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const count = parsedHTML.getElementById('ProductCount')?.innerHTML;
    const countDesktop = parsedHTML.getElementById('ProductCountDesktop')?.innerHTML;
    const drawerViewLabel = parsedHTML.getElementById('FilterDrawerViewLabel')?.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    const drawerViewLabelEl = document.getElementById('FilterDrawerViewLabel');
    const drawerClearAll = document.getElementById('FilterDrawerClearAll');
    const sourceClearAll = parsedHTML.getElementById('FilterDrawerClearAll');

    if (container && count !== undefined) {
      container.innerHTML = count;
    }

    if (containerDesktop && countDesktop !== undefined) {
      containerDesktop.innerHTML = countDesktop;
    } else if (containerDesktop && count !== undefined) {
      containerDesktop.innerHTML = count;
    }

    if (drawerViewLabelEl && drawerViewLabel !== undefined) {
      drawerViewLabelEl.innerHTML = drawerViewLabel;
    }

    if (drawerClearAll && sourceClearAll) {
      drawerClearAll.classList.toggle('hidden', sourceClearAll.classList.contains('hidden'));
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const filterSelector =
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FilterDrawer .js-filter';
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(filterSelector);

    if (!facetDetailsElementsFromFetch.length || !parsedHTML.getElementById('ProductGridContainer')) return;

    const facetDetailsElementsFromDom = document.querySelectorAll(filterSelector);

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        FacetFiltersForm.updateFilterElement(currentElement, elementToRender);
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.reinitPriceRanges();

    if (countsToRender && event?.target?.closest) {
      const activeFilter = event.target.closest('.js-filter');

      if (activeFilter?.id) {
        FacetFiltersForm.renderCounts(countsToRender, activeFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(activeFilter.id));

        const newFacetDetailsElement = document.getElementById(activeFilter.id);
        const newElementSelector = newFacetDetailsElement?.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement?.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.syncFilterDrawerFormAssociation();
  }

  static isPriceFilter(element) {
    return Boolean(element?.querySelector('price-range'));
  }

  static isFilterActivelyEditing(element) {
    const activeElement = document.activeElement;
    if (!activeElement || !element?.contains(activeElement)) return false;

    if (activeElement.closest('facet-remove')) return false;

    const type = (activeElement.getAttribute('type') || activeElement.type || '').toLowerCase();
    return (
      activeElement.tagName === 'TEXTAREA' ||
      (activeElement.tagName === 'INPUT' && ['text', 'number', 'search', ''].includes(type))
    );
  }

  static updatePriceRangeElement(currentElement, sourceElement) {
    const currentPriceRange = currentElement.querySelector('price-range');
    const sourcePriceRange = sourceElement.querySelector('price-range');

    if (currentPriceRange && sourcePriceRange) {
      currentPriceRange.replaceWith(document.importNode(sourcePriceRange, true));
      return;
    }

    currentElement.innerHTML = sourceElement.innerHTML;
  }

  static reinitPriceRanges(root = document) {
    root.querySelectorAll('price-range').forEach((priceRange) => {
      if (typeof priceRange.reinit === 'function') {
        priceRange.reinit();
      }
    });
  }

  static isShowingMore(filterElement) {
    return Boolean(filterElement?.querySelector('show-more-button .label-show-more.hidden'));
  }

  static expandShowMoreItems(container) {
    if (!container) return;
    container.querySelectorAll('.show-more-item.hidden').forEach((item) => {
      item.classList.remove('hidden');
    });
  }

  static syncShowMoreButton(currentElement, sourceElement, isShowingMore) {
    const targetButton = currentElement.querySelector('show-more-button');
    const sourceButton = sourceElement.querySelector('show-more-button');

    if (!sourceButton && targetButton) {
      targetButton.remove();
      return;
    }

    if (sourceButton && !targetButton) {
      const parentDisplay = currentElement.querySelector('.parent-display');
      parentDisplay?.appendChild(document.importNode(sourceButton, true));
      return;
    }

    if (!targetButton) return;

    const moreLabel = targetButton.querySelector('.label-show-more');
    const lessLabel = targetButton.querySelector('.label-show-less');
    if (!moreLabel || !lessLabel) return;

    moreLabel.classList.toggle('hidden', isShowingMore);
    lessLabel.classList.toggle('hidden', !isShowingMore);
  }

  static updateFilterElement(currentElement, sourceElement) {
    if (FacetFiltersForm.isFilterActivelyEditing(currentElement)) {
      return;
    }

    if (FacetFiltersForm.isPriceFilter(currentElement)) {
      FacetFiltersForm.updatePriceRangeElement(currentElement, sourceElement);
      return;
    }

    const filterDrawer = document.getElementById('FilterDrawer');
    const inOpenDrawer =
      filterDrawer?.classList.contains('filter-drawer--active') && filterDrawer.contains(currentElement);

    if (inOpenDrawer) {
      const sourceWrap = sourceElement.querySelector('.facets-wrap');
      const targetWrap = currentElement.querySelector('.facets-wrap');
      const sourceHeader = sourceElement.querySelector('.facets__header');
      const targetHeader = currentElement.querySelector('.facets__header');
      const isShowingMore = FacetFiltersForm.isShowingMore(currentElement);

      if (sourceHeader && targetHeader) {
        targetHeader.innerHTML = sourceHeader.innerHTML;
      }

      if (sourceWrap && targetWrap) {
        if (isShowingMore) {
          FacetFiltersForm.expandShowMoreItems(sourceWrap);
        }
        targetWrap.innerHTML = sourceWrap.innerHTML;
      }

      FacetFiltersForm.syncShowMoreButton(currentElement, sourceElement, isShowingMore);

      return;
    }

    currentElement.innerHTML = sourceElement.innerHTML;
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
  }

  static syncFilterDrawerFormAssociation(root = document.getElementById('FilterDrawer')) {
    if (!root) return;

    root.querySelectorAll('input, select, textarea').forEach((element) => {
      if (!element.getAttribute('form')) {
        element.setAttribute('form', 'FacetFiltersForm');
      }
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    const mobileMenuDrawer = mobileFacetForm?.closest('menu-drawer');
    if (mobileMenuDrawer?.bindEvents) mobileMenuDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummaryLabel = target.querySelector('.facets__summary-label');
    const sourceSummaryLabel = source.querySelector('.facets__summary-label');

    if (sourceSummaryLabel && targetSummaryLabel) {
      targetSummaryLabel.innerHTML = sourceSummaryLabel.innerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.innerHTML = sourceHeaderElement.innerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = FacetFiltersForm.isShowingMore(target);
      if (isShowingMore) {
        FacetFiltersForm.expandShowMoreItems(sourceWrapElement);
      }

      targetWrapElement.innerHTML = sourceWrapElement.innerHTML;
      FacetFiltersForm.syncShowMoreButton(target, source, isShowingMore);
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      const isShowingMore = FacetFiltersForm.isShowingMore(target);
      if (isShowingMore) {
        FacetFiltersForm.expandShowMoreItems(sourceFacetsList);
      }

      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
      FacetFiltersForm.syncShowMoreButton(target, source, isShowingMore);
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value === '' || value == null) continue;
      searchParams.append(key, value);
    }

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const targetForm = event.target.closest('form') || document.getElementById('FacetFiltersForm');
      const isMobile = targetForm?.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.filter(Boolean).join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const link = event.currentTarget?.closest('a') || event.currentTarget;
    FacetFiltersForm.handleFilterRemove(link);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.drawerInputBound = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.onRangeInput = this.onRangeInput.bind(this);
    this.onTextInput = this.onTextInput.bind(this);
    this.onTextChange = this.onTextChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMaxKeyUp = this.onMaxKeyUp.bind(this);
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  reinit() {
    this.unbindEvents();
    this.bindEvents();
  }

  bindEvents() {
    this.unbindEvents();

    this.rangeMin = this.querySelector('.price-range-widget__range--min');
    this.rangeMax = this.querySelector('.price-range-widget__range--max');
    this.rangeControl = this.rangeMax || this.querySelector('.price-range-widget__range');
    this.fill = this.querySelector('.price-range-widget__fill');
    this.minInput = this.querySelector('.price-range-widget__input--min');
    this.maxInput = this.querySelector('.price-range-widget__input--max');

    if (this.minInput && this.maxInput && (this.rangeMin || this.rangeControl)) {
      this.initPriceRangeWidget();
      return;
    }

    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onTextChange);
      element.addEventListener('keydown', this.onKeyDown);
    });
  }

  unbindEvents() {
    this.rangeMin?.removeEventListener('input', this.onRangeInput);
    this.rangeMax?.removeEventListener('input', this.onRangeInput);
    this.rangeControl?.removeEventListener('input', this.onRangeInput);

    this.textInputs?.forEach((input) => {
      input.removeEventListener('input', this.onTextInput);
      input.removeEventListener('change', this.onTextChange);
      input.removeEventListener('keydown', this.onKeyDown);
    });

    this.maxInput?.removeEventListener('keyup', this.onMaxKeyUp);
  }

  initPriceRangeWidget() {
    const inner = this.querySelector('.price-range-widget__inner');
    this.rangeCeiling = this.parseValue(inner?.dataset.rangeMax) || this.parseValue(this.maxInput.dataset.max);
    this.textInputs = [this.minInput, this.maxInput];

    if (this.rangeMin) {
      this.rangeMin.addEventListener('input', this.onRangeInput);
    }
    if (this.rangeMax) {
      this.rangeMax.addEventListener('input', this.onRangeInput);
    } else if (this.rangeControl) {
      this.rangeControl.addEventListener('input', this.onRangeInput);
    }

    this.textInputs.forEach((input) => {
      input.addEventListener('input', this.onTextInput);
      input.addEventListener('change', this.onTextChange);
      input.addEventListener('keydown', this.onKeyDown);
    });

    this.maxInput.addEventListener('keyup', this.onMaxKeyUp);

    this.syncSliderFromText();
    this.updateFill();
    this.updateRangeZIndex();
  }

  getInputBounds(input) {
    const ceiling = this.getRangeCeiling();
    const min = this.parseValue(input?.getAttribute('data-min'));
    const max = this.parseValue(input?.getAttribute('data-max')) || ceiling;

    return {
      min: Number.isFinite(min) ? min : 0,
      max: Number.isFinite(max) ? max : ceiling,
    };
  }

  parseValue(value) {
    if (value === undefined || value === null || value === '') return 0;
    return Number(String(value).replace(/,/g, ''));
  }

  getRangeCeiling() {
    return this.rangeCeiling || this.parseValue(this.maxInput?.dataset.max);
  }

  getMinValue() {
    return this.parseValue(this.minInput.value);
  }

  getMaxValue() {
    const ceiling = this.getRangeCeiling();
    if (this.maxInput.value === '') return ceiling;
    return this.parseValue(this.maxInput.value);
  }

  onRangeInput(event) {
    const ceiling = this.getRangeCeiling();
    const control = event?.currentTarget || this.rangeMax || this.rangeControl;
    let min = this.getMinValue();
    let max = this.getMaxValue();

    if (this.rangeMin && control === this.rangeMin) {
      min = Math.max(0, Math.min(this.parseValue(this.rangeMin.value), ceiling));
      if (min > max) {
        max = min;
        if (this.rangeMax) this.rangeMax.value = max;
        this.maxInput.value = max >= ceiling ? '' : max;
      }
      this.rangeMin.value = min;
      this.minInput.value = min > 0 ? min : '';
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      max = Math.max(0, Math.min(this.parseValue((this.rangeMax || this.rangeControl).value), ceiling));
      if (max < min) {
        min = max;
        if (this.rangeMin) this.rangeMin.value = min;
        this.minInput.value = min > 0 ? min : '';
      }
      if (this.rangeMax) this.rangeMax.value = max;
      else if (this.rangeControl) this.rangeControl.value = max;
      this.maxInput.value = max >= ceiling ? '' : max;
      this.maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.updateFill();
    this.updateRangeZIndex();
  }

  onTextInput() {
    this.syncSliderFromText();
    this.updateFill();
    this.updateRangeZIndex();
  }

  onTextChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.syncSliderFromText();
    this.updateFill();
    this.updateRangeZIndex();
  }

  syncSliderFromText() {
    const ceiling = this.getRangeCeiling();
    let min = this.getMinValue();
    let max = this.getMaxValue();

    min = Math.max(0, Math.min(min, ceiling));
    max = Math.max(0, Math.min(max, ceiling));

    const sliderMax = Math.max(min, max);

    if (this.rangeMin) {
      this.rangeMin.max = ceiling;
      this.rangeMin.value = min;
    }

    if (this.rangeMax) {
      this.rangeMax.max = ceiling;
      this.rangeMax.value = sliderMax;
    } else if (this.rangeControl) {
      this.rangeControl.max = ceiling;
      this.rangeControl.value = sliderMax;
    }
  }

  updateRangeZIndex() {
    if (!this.rangeMin || !this.rangeMax) return;

    const ceiling = this.getRangeCeiling();
    const min = this.getMinValue();
    const max = this.getMaxValue();
    const midpoint = ceiling / 2;

    if (min > midpoint || min >= max - 1) {
      this.rangeMin.style.zIndex = 5;
      this.rangeMax.style.zIndex = 4;
    } else {
      this.rangeMin.style.zIndex = 3;
      this.rangeMax.style.zIndex = 4;
    }
  }

  updateFill() {
    if (!this.fill) return;

    const ceiling = this.getRangeCeiling();
    if (!ceiling) return;

    const min = this.getMinValue();
    const max = this.getMaxValue();
    const left = (min / ceiling) * 100;
    const width = ((max - min) / ceiling) * 100;

    this.fill.style.left = `${left}%`;
    this.fill.style.width = `${width}%`;
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  onMaxKeyUp() {
    const max = this.getInputBounds(this.maxInput).max;
    if (this.maxInput.value === '' || !Number.isFinite(max)) return;
    if (this.parseValue(this.maxInput.value) <= max) return;

    this.maxInput.value = max;
    this.syncSliderFromText();
    this.updateFill();
    this.updateRangeZIndex();
  }

  adjustToValidValues(input) {
    if (!input) return;

    const value = this.parseValue(input.value);
    const bounds = this.getInputBounds(input);

    if (input.value !== '' && value < bounds.min) input.value = bounds.min;
    if (input.value !== '' && value > bounds.max) input.value = bounds.max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.closeFilter = this.closeFilter.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  connectedCallback() {
    const facetLink = this.querySelector('a');
    if (!facetLink || facetLink.dataset.facetRemoveBound === 'true') return;

    facetLink.dataset.facetRemoveBound = 'true';
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter);
    facetLink.addEventListener('keyup', this.onKeyUp);
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'SPACE') {
      event.preventDefault();
      this.closeFilter(event);
    }
  }

  closeFilter(event) {
    event.preventDefault();
    if (FacetRemove.isNoOp(event.currentTarget)) return;
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form?.onActiveFilterClick(event);
  }

  static isNoOp(link) {
    if (!link?.href) return false;
    return FacetRemove.filterKey(new URL(link.href)) === FacetRemove.filterKey(new URL(window.location.href));
  }

  static filterKey(url) {
    const params = new URLSearchParams(url.search);
    params.delete('page');
    params.sort();
    return `${url.pathname}?${params.toString()}`;
  }
}

customElements.define('facet-remove', FacetRemove);
